import { Component, Directive, HostBinding, Input } from "@angular/core";
import { Constructor } from "@angular/material/core/common-behaviors/constructor";

export type Spacing =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

export interface DimensionSpacings {
  t?: Spacing;
  b?: Spacing;
  l?: Spacing;
  r?: Spacing;

  // set padding-left and padding-right
  h?: Spacing;
  // set padding-top and padding-bottom
  v?: Spacing;
}

// todo: figure out return type for mixins
// tslint:disable-next-line:typedef
export function WithPadding<Base extends Constructor<Component>>(
  BaseClass: Base
) {
  @Directive()
  // tslint:disable-next-line:class-name
  class _WithPadding extends BaseClass {
    @Input()
    p: Spacing;

    @Input()
    ph: Spacing;

    @Input()
    pv: Spacing;

    @Input()
    pt: Spacing;

    @Input()
    pr: Spacing;

    @Input()
    pb: Spacing;

    @Input()
    pl: Spacing;

    @Input()
    set padding(val: Spacing | Spacing[] | DimensionSpacings | string) {
      if (isNumericSpacing(val)) {
        this._padding = val;
      }

      if (isDecomposableStringSpacing(val)) {
        this._padding = decomposeStringSpacing(val);
      }

      if (Array.isArray(val)) {
        this._padding = spacingsArrayToDimensions(val);
      }
    }
    private _padding: Spacing | DimensionSpacings;

    protected get paddingClassList(): string[] {
      return spacingToPaddingClassList(this._padding);
    }
  }

  return _WithPadding;
}

@Component({
  selector: "exp-padding-container, [exp-padding-container]",
  template: `<ng-content></ng-content>`
})
export class PaddingContainerComponent extends WithPadding(class {}) {
  @HostBinding("class")
  get hostClass(): string {
    return this.paddingClassList
      .filter((className) => className != null)
      .join(" ");
  }
}

// todo: figure out return t\ype of mixin
// tslint:disable-next-line:typedef
export function WithMargin<Base extends Constructor<Component>>(
  BaseClass: Base
) {
  @Directive()
  // tslint:disable-next-line:class-name
  class _WithMargin extends BaseClass {
    @Input()
    m: Spacing;

    @Input()
    mh: Spacing;

    @Input()
    mv: Spacing;

    @Input()
    mt: Spacing;

    @Input()
    mr: Spacing;

    @Input()
    mb: Spacing;

    @Input()
    ml: Spacing;

    @Input()
    set margin(val: Spacing | Spacing[] | DimensionSpacings | string) {
      if (isNumericSpacing(val)) {
        this._margin = val;
      }

      if (isDecomposableStringSpacing(val)) {
        this._margin = decomposeStringSpacing(val);
      }

      if (Array.isArray(val)) {
        this._margin = spacingsArrayToDimensions(val);
      }
    }
    private _margin: Spacing | DimensionSpacings;

    protected get marginClassList(): string[] {
      return spacingToMarginClassList(this._margin);
    }
  }

  return _WithMargin;
}

@Component({
  selector: "exp-margin-container, [exp-margin-container]",
  template: `<ng-content></ng-content>`
})
export class MarginContainerComponent extends WithMargin(class {}) {
  @HostBinding("class")
  get hostClass(): string {
    return this.marginClassList
      .filter((className) => className != null)
      .join(" ");
  }
}

// tslint:disable-next-line:typedef
export function WithSpacing<Base extends Constructor<Component>>(
  BaseClass: Base
) {
  return WithMargin(WithPadding(BaseClass));
}

@Component({
  selector: "exp-spacing-container, [exp-spacing-container]",
  template: `<ng-content></ng-content>`
})
export class SpacingContainerComponent extends WithSpacing(class {}) {
  @HostBinding("class")
  get hostClass(): string {
    return [...this.paddingClassList, ...this.marginClassList]
      .filter((className) => className != null)
      .join(" ");
  }
}

function spacingToPaddingClassList(
  spacing: Spacing | Spacing[] | DimensionSpacings
): string[] {
  return spacingToClassList(spacing, "p");
}

function spacingToMarginClassList(
  spacing: Spacing | Spacing[] | DimensionSpacings
): string[] {
  return spacingToClassList(spacing, "m");
}

function spacingToClassList(
  spacing: Spacing | Spacing[] | DimensionSpacings,
  space: "p" | "m"
): string[] {
  if (spacing == null) {
    return [];
  }

  if (isNumericSpacing(spacing)) {
    return [`${space}--${spacing}`];
  }

  if (isDecomposableStringSpacing(spacing)) {
    return spacingToClassList(spacing, space);
  }

  if (Array.isArray(spacing)) {
    return spacing.length === 0
      ? []
      : spacing.length === 1
      ? spacingToClassList(spacing[0], space)
      : spacing.length === 2
      ? spacingToClassList({ v: spacing[0], h: spacing[1] }, space)
      : spacingToClassList(
          { t: spacing[0], r: spacing[1], b: spacing[2], l: spacing[3] },
          space
        );
  }

  return Object.entries(spacing).map(
    ([dimension, rems]) => `${space}${dimension}--${rems}`
  );
}

// turn "1,2,3,4", "1 2 3 4", "1 2", "1" into Spacing[]
// works like css
// padding: 1px 2px 3px 4px -- all 4 sides individual
// padding: 1px 2px 3px -- top 1, horizontal(left and right) 2, bottom 3
// padding: 1px 2px -- vertical(top and bottom) 1, horizontal(left and right) 2
// padding: 1px -- all 4 sides 1px
function decomposeStringSpacing(spacing: string): Spacing | DimensionSpacings {
  const spacings = spacing
    .split(/[, ]/)
    .map((s) => s.trim())
    .filter((s) => s !== "")
    .map((s) => Number(s)) as Spacing[];

  if (spacings.length === 1) {
    return spacings[0];
  }

  if (spacings.length === 2) {
    return { v: spacings[0], h: spacings[1] };
  }

  if (spacings.length === 3) {
    return { t: spacings[0], h: spacings[1], b: spacings[2] };
  }

  return {
    t: spacings[0],
    r: spacings[1],
    b: spacings[2],
    l: spacings[3]
  };
}

function spacingsArrayToDimensions(
  spacings: Spacing[]
): Spacing | DimensionSpacings {
  if (spacings.length === 0) {
    return {};
  }

  if (spacings.length === 1) {
    return spacings[0];
  }

  if (spacings.length === 2) {
    return { v: spacings[0], h: spacings[1] };
  }
  if (spacings.length === 3) {
    return { t: spacings[0], h: spacings[1], b: spacings[2] };
  }

  return {
    t: spacings[0],
    r: spacings[1],
    b: spacings[2],
    l: spacings[3]
  };
}

function isNumericSpacing(spacing: any): spacing is Spacing {
  if (typeof spacing !== "number" && typeof spacing !== "string") {
    return false;
  }

  const spacingNumber = Number(spacing);

  return (
    !isNaN(spacingNumber) &&
    Number.isInteger(spacingNumber) &&
    spacingNumber >= 0 &&
    spacingNumber <= 10
  );
}

function isDecomposableStringSpacing(spacing: any): spacing is string {
  if (typeof spacing !== "string") {
    return false;
  }

  const regex = /^\s*\d+(\s*[, ]\s*\d+){0,3}\s*$/;
  return regex.test(spacing);
}
